import getConfig from 'next/config';
import { RefObject } from 'react';
import { ActionDisclaimer } from '../action-utilities/action-disclaimer';
import { EditComment } from '../action-utilities/edit-comment';
import { ICommentContext } from '../community-context/comment-context';
import CommunityQAServices from '../services/community-qa.service';
import { CommentTemplate } from './constant';
import {
  ActionButtonTypes,
  FeedbackState,
  IActionApiCallParams,
  IContent,
  IParentProps,
  StatusType,
} from './types';

const { publicRuntimeConfig } = getConfig();

interface IActionApiCall extends Partial<ICommentContext> {
  replies?: IParentProps;
  setReplies?: (value: IParentProps) => void;
  setIsLoading: (value: boolean) => void;
}

export const textToBase64 = (text: string) => {
  if (typeof window !== 'undefined' && text) {
    return btoa(text);
  }
  return '';
};

export const generateSSOLinkWithReturnPath = (
  returnPath: string,
  product: string
) => {
  return `${publicRuntimeConfig.SSO_ENDPOINT}?return_path=${textToBase64(
    returnPath
  )}&product=${product}`;
};

export const actionApiCall = async (
  actionBtn: string,
  commentId: number,
  setDisplayActionModal: (value: boolean) => void,
  ctxObject: IActionApiCall,
  content?: IContent,
  setCommentContent?: (value: IContent) => void
) => {
  const { parentComment, setIsLoading, setParentComment, replies, setReplies } =
    ctxObject;
  if (actionBtn === ActionButtonTypes.Delete) {
    CommunityQAServices.deleteComment(commentId).then((response) => {
      if (response.status === StatusType.Success) {
        setDisplayActionModal(false);
        setIsLoading(false);

        // delete parent comment
        if (setParentComment && parentComment) {
          const updatedComments = parentComment?.results?.filter(
            (comment) => comment.id !== commentId
          );
          setParentComment({ ...parentComment, results: updatedComments });
        }

        // delete comment from replies
        if (setReplies && replies) {
          const updatedReplies = replies?.results.filter(
            (comment) => comment.id !== commentId
          );
          setReplies({ ...replies, results: updatedReplies });
        }
      }
    });
  } else if (actionBtn === ActionButtonTypes.Report) {
    CommunityQAServices.reportComment(commentId).then((response) => {
      if (
        response.status === StatusType.Success &&
        setCommentContent &&
        content
      ) {
        setCommentContent({
          ...content,
          reported: true,
        });
      }
    });
    setDisplayActionModal(false);
    setIsLoading(false);
  }
};

export const resizeBox = (textAreaRef: RefObject<HTMLTextAreaElement>) => {
  if (textAreaRef.current) {
    textAreaRef.current.style.height = 'auto';
    textAreaRef.current.style.height = textAreaRef.current.scrollHeight + 'px';
  }
};

export const actionModalHandler = ({
  targetId,
  actionObj,
  replies = CommentTemplate,
  setReplies = () => {},
}: IActionApiCallParams) => {
  actionObj.setDisplayActionModal(true);

  switch (targetId) {
    case 'edit':
      return (
        <EditComment
          content={actionObj.content}
          setCommentContent={actionObj.setCommentContent}
          commentId={actionObj.commentId}
          setDisplayActionModal={actionObj.setDisplayActionModal}
        />
      );
    case 'delete':
      return (
        <ActionDisclaimer
          setDisplayActionModal={actionObj.setDisplayActionModal}
          actionBtn={ActionButtonTypes.Delete}
          commentId={actionObj.commentId}
          replies={replies}
          setReplies={setReplies}
        />
      );
    case 'report':
      return (
        <ActionDisclaimer
          content={actionObj.content}
          setCommentContent={actionObj.setCommentContent}
          setDisplayActionModal={actionObj.setDisplayActionModal}
          actionBtn={ActionButtonTypes.Report}
          commentId={actionObj.commentId}
        />
      );
    default:
      return <div>Inappropriate action</div>;
  }
};

export const handleFeedback = ({
  type,
  feedback,
  setFeedback,
  id,
}: {
  type: boolean;
  feedback: FeedbackState;
  setFeedback: (value: FeedbackState) => void;
  id: number;
}) => {
  const payload = {
    status: type,
  };

  // Increment or decrement 'like' and 'dislike' based on 'type'
  const updatedLike = type
    ? feedback.like + 1
    : feedback.like === 0
    ? 0
    : feedback.like - 1;

  const updatedDislike = type
    ? feedback.dislike === 0
      ? 0
      : feedback.dislike - 1
    : feedback.dislike + 1;

  // Set 'status' based on 'type'
  const updatedStatus = type ? true : false;

  // Update the feedback object
  setFeedback({
    like: updatedLike,
    dislike: updatedDislike,
    status: updatedStatus,
  });

  CommunityQAServices.submitFeedback(id, payload).then((response) => {
    if (response?.status !== StatusType.Success) {
      console.log('Something went wrong !!!');
    }
  });
};

export const getPageNumber = (link: string | null) => {
  const PAGE_NUMBER = 1;
  if (!link) {
    return PAGE_NUMBER;
  }
  // Regular expression to extract the page number
  const pageNumberRegex = /page=(\d+)/;

  // Extracting the page number
  const pageNumberMatch = link.match(pageNumberRegex);
  return pageNumberMatch ? Number(pageNumberMatch[1]) : PAGE_NUMBER;
};
